import {  useNotify, useRedirect } from "react-admin";
import { useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  AppCreate,
  AppSimpleForm,
  AppFormToolbar,
  AppFormSection,
  SectionHeader,
  AppFormContent,
  AppTextInput,
  AppReferenceInput,
  AppRadioButtonGroupInput
} from "@/adapters/inbound/layout";
import { useDI } from "@/infrastructure/di/useDI";
import {
  constructionSiteIntakeFormSchema,
  type ConstructionSiteIntakeFormInput,
} from "../schemas";
import { toCreateConstructionSiteIntakeCommand } from "../mappers";

export const defaultConstructionSiteIntakeFormValues: ConstructionSiteIntakeFormInput =
  {
    client: {
      mode: "existing",
      clientId: "",
    },
    constructionSite: {
      label: "",
      address: {
        mode: "existing_address",
        addressId: "",
      },
    },
  };

function ClientSection() {
  const mode = useWatch<ConstructionSiteIntakeFormInput>({ name: "client.mode" });

  return (
    <>
      <AppRadioButtonGroupInput
        source="client.mode"
        label="Kunde"
        choices={[
          { id: "existing", name: "Bestehenden Kunden wählen" },
          { id: "new", name: "Neuen Kunden anlegen" },
        ]}
      />

      {mode === "existing" ? (
        <AppReferenceInput
          source="client.clientId"
          reference="clients"
          label="Kunde"
          isRequired
          autocompleteProps={{
            optionText: "name",
            fullWidth: true,
          }}
        />
      ) : (
        <>
          <AppTextInput source="client.name" label="Name" required fullWidth />
          <AppTextInput
            source="client.contactName"
            label="Ansprechpartner"
            fullWidth
          />
          <AppTextInput source="client.phone" label="Telefon" fullWidth />
          <AppTextInput source="client.email" label="E-Mail" fullWidth />
          <AppTextInput
            source="client.address.label"
            label="Adresszusatz"
            fullWidth
          />
          <AppTextInput
            source="client.address.street"
            label="Straße"
            required
            fullWidth
          />
          <AppTextInput
            source="client.address.streetNumber"
            label="Nr."
            required
          />
          <AppTextInput source="client.address.zip" label="PLZ" required />
          <AppTextInput
            source="client.address.city"
            label="Ort"
            required
            fullWidth
          />
          <AppTextInput
            source="client.address.country"
            label="Land"
            required
            defaultValue="DE"
          />
        </>
      )}
    </>
  );
}

function ConstructionSiteSection() {
  const clientMode = useWatch<ConstructionSiteIntakeFormInput>({
    name: "client.mode",
  });
  const clientId = useWatch<ConstructionSiteIntakeFormInput>({
    name: "client.clientId",
  });
  const addressMode = useWatch<ConstructionSiteIntakeFormInput>({
    name: "constructionSite.address.mode",
  });

  const addressChoices =
    clientMode === "existing"
      ? [
          { id: "existing_address", name: "Bestehende Adresse wählen" },
          { id: "client_address", name: "Kundenadresse verwenden" },
          { id: "new_address", name: "Andere Adresse eingeben" },
        ]
      : [
          { id: "client_address", name: "Kundenadresse verwenden" },
          { id: "new_address", name: "Andere Adresse eingeben" },
        ];

  return (
    <>
      <AppTextInput
        source="constructionSite.label"
        label="Bezeichnung des Einsatzorts"
        helperText="Optional"
        fullWidth
      />

      <AppRadioButtonGroupInput
        source="constructionSite.address.mode"
        label="Adresse"
        choices={addressChoices}
      />

      {addressMode === "existing_address" && clientMode === "existing" ? (
        <AppReferenceInput
          source="constructionSite.address.addressId"
          reference="addresses"
          label="Adresse"
          filter={{ clientId }}
          isRequired
          autocompleteProps={{
            optionText: (record: any) =>
              [record?.label, record?.street, record?.streetNumber, record?.zip, record?.city]
                .filter(Boolean)
                .join(", "),
            fullWidth: true,
          }}
        />
      ) : null}

      {addressMode === "new_address" ? (
        <>
          <AppTextInput
            source="constructionSite.address.address.label"
            label="Adresszusatz"
            fullWidth
          />
          <AppTextInput
            source="constructionSite.address.address.street"
            label="Straße"
            required
            fullWidth
          />
          <AppTextInput
            source="constructionSite.address.address.streetNumber"
            label="Nr."
            required
          />
          <AppTextInput
            source="constructionSite.address.address.zip"
            label="PLZ"
            required
          />
          <AppTextInput
            source="constructionSite.address.address.city"
            label="Ort"
            required
            fullWidth
          />
          <AppTextInput
            source="constructionSite.address.address.country"
            label="Land"
            required
            defaultValue="DE"
          />
        </>
      ) : null}
    </>
  );
}

export function ConstructionSiteCreate() {
  const notify = useNotify();
  const redirect = useRedirect();
  const { createConstructionSiteIntake } = useDI();

  const onSubmit = async (values: any) => {
    try {
      const cmd = toCreateConstructionSiteIntakeCommand(values);
      const result = await createConstructionSiteIntake.execute(cmd);
      notify("Einsatzort erstellt", { type: "success" });
      redirect("show", "constructionSites", result.id);
    } catch (error: any) {
      notify(error?.message ?? "Einsatzort konnte nicht erstellt werden", {
        type: "error",
      });
    }
  };

  return (
    <AppCreate>
      <AppSimpleForm
        defaultValues={defaultConstructionSiteIntakeFormValues}
        resolver={zodResolver(constructionSiteIntakeFormSchema) as any}
        mode="onBlur"
        shouldUnregister
        toolbar={<AppFormToolbar showDelete={false} />}
        onSubmit={onSubmit}
      >
        <AppFormSection>
          <SectionHeader
            title="Kunde und Einsatzort"
            subtitle="Wählen Sie vorhandene Daten oder erfassen Sie neue direkt hier."
          />
          <AppFormContent>
            <ClientSection />
            <ConstructionSiteSection />
          </AppFormContent>
        </AppFormSection>
      </AppSimpleForm>
    </AppCreate>
  );
}
